<template>
  <Modal
    v-model="modalVisible"
    :cancelText="numEligibleUsers ? 'cancel' : ''"
    :handleMainAction="handleMainAction"
    headerText="Require MFA on User Portal"
    :mainActionText="numEligibleUsers ? 'require' : 'ok'"
  >
    <div v-if="numEligibleUsers">
      <p>
        {{ numEligibleUsers }} of {{ selectedItems.length }} selected users
        are eligible for MFA requirement on User Portal.
        <span v-if="numEligibleUsers < selectedItems.length">
          Selected users that are already required will not be modified.
        </span>
      </p>
      <p>
        Users with MFA required on User Portal will be prompted to set it up every
        time they log in to the User Portal during their enrollment period. Beyond
        their enrollment period, or upon setup, they will be required to verify
        their MFA to log in to User Portal, as well as on any bound system that has
        MFA enabled.
      </p>
      <MFAEnrollmentInput
        v-model="mfaEnrollmentValue"
        keyword="users requiring setup"
      />
    </div>
    <div v-else>
      <p>
        Selected users are already required and will not be modified.
      </p>
    </div>
  </Modal>
</template>

<script>
import * as Constants from 'client/util/Constants';
import { get } from 'lodash';
import { mapActions, mapGetters } from 'vuex';
import { notify } from 'client/util';
import MFAEnrollmentInput from 'components/Modal/mfaModals/MFAEnrollmentInput.vue';
import mfaEnrollmentMixin from 'components/mixins/mfaEnrollmentMixin';
import mfaModalMixin from 'components/Modal/mfaModals/mfaModalMixin';
import pluralize from 'pluralize';

const { error, success } = Constants.default;

export default {
  name: 'UserEnableMFAModal',

  components: {
    MFAEnrollmentInput,
  },

  mixins: [
    mfaEnrollmentMixin,
    mfaModalMixin,
  ],

  data: () => ({
    adUsers: [],
    alreadyActiveUsers: [],
  }),

  computed: {
    ...mapGetters('SystemUserCollection', {
      ineligibleUsers: 'getUsersIneligibleForMFAEnablement',
      selectedItems: 'getSelectedItems',
    }),

    numEligibleUsers() {
      const { alreadyActiveUsers } = this.ineligibleUsers;
      return this.selectedItems.length - alreadyActiveUsers.length;
    },
  },

  methods: {
    ...mapActions('SystemUserCollection', [
      'bulkRequireMFA',
      'unselectItems',
    ]),

    /**
     * Enables MFA for users that are selected
     */
    enableMFAForUsers() {
      const numUsers = this.selectedItems.length;

      this.bulkRequireMFA({
        ids: this.selectedItems,
        exclusionUntil: this.getExclusionUntil(),
        exclusionDays: this.exclusionDays(),
      })
        .then((response) => {
          if (get(response, 'numSuccess')) {
            if (numUsers === 1) {
              notify(success, '1 user was successfully updated.');
            } else {
              notify(success, `${response.numSuccess} of ${numUsers}`
                + ' users were successfully updated.');
            }
            this.unselectItems({ ids: this.selectedItems });
          } else {
            const usersNoun = pluralize('user', numUsers);
            notify(error, `Error updating ${usersNoun}.`);
          }
        });
    },

    /**
     * Executes when the user clicks the main action button in the modal.
     * Validates the MFA enrollment period value prior to enabling MFA
     * for the selected users. If there are users that cannot have MFA
     * enabled (AD users and already MFA-active users), they are unselected.
     */
    handleMainAction() {
      if (this.v2Errors.items.length) {
        notify(error, 'Please provide a valid enrollment period value.');
      } else {
        const { alreadyActiveUsers } = this.ineligibleUsers;

        if (this.numEligibleUsers) {
          if (alreadyActiveUsers.length) {
            this.unselectItems({ ids: alreadyActiveUsers.map(item => item.id) });
          }
          this.enableMFAForUsers();
        }

        /**
         * Close the modal and reset the mfa enrollment value to the default
         * for the next time the modal is displayed.
         */
        this.hideModal();
        this.resetMfaEnrollmentValue();
      }
    },
  },
};
</script>
